<template>
  <div
    class="sequence-detail-output overflow-y-scroll custom_scrollbar flex flex-col pr-4 gap-4"
  >
    <ViewFilterComponent
      source="sequence_linkedin_logs"
      :filters-options="linkedInLogFiltersOptions"
      :filter-data="linkedInLogFilterData"
      @filter-changed="handleLinkedInLogFilterChange"
      :show-clear-all="true"
      @clear-all-filters="clearAllFilters"
    />

    <div class="flex flex-col">
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
        <CustomLoader :size="28" />
      </div>

      <LinkedinLogsTable
        v-else-if="linkedInLogs.length > 0"
        :loading="loading"
        :pagination="pagination"
        :linked-in-logs="linkedInLogs"
        :handlePageChange="handlePageChange"
        :source="entityTypes.SEQUENCE_LINKEDIN_LOGS"
        :selection="selection"
        @update:selection="handleSelectionUpdate"
      />

      <EmptyScreen
        v-else
        padding="py-36"
        source="sequence_linkedin_logs"
        text="No LinkedIn logs found!"
        description="Start sending LinkedIn messages to see them here"
        :type="emptyScreenTypes.SECONDARY"
      />
    </div>

    <ExportModal
      :visible="showExportModal"
      :title="`Export ${featureNames.SEQUENCES} LinkedIn Logs`"
      :description="`Selected ${featureNames.SEQUENCES.toLowerCase()} LinkedIn logs will be exported to your email`"
      :selection="selection"
      :export-function="
        (exportData) => DashboardService.exportLinkedInLogs(
          exportData, 
          transformFiltersForBackend({
            ...linkedInLogFilterData,
            sequence_id: props.sequenceId
              ? [props.sequenceId]
              : linkedInLogFilterData?.sequence_id || [],
          })
        )
      "
      @update:visible="showExportModal = $event"
      @export-success="handleExportSuccess"
    />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";
import { updateFilterOptions } from "@/src/utils/utils.js";
import { message } from "ant-design-vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { sequenceLinkedInLogFilters } from "@/src/constants/filterConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import DashboardService from "@/src/api/DashboardService.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LinkedinLogsTable from "@/src/components/linkedin_logs/LinkedinLogsTable.vue";
import ViewFilterComponent from "@/src/components/view_components/ViewFilterComponent.vue";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import ExportModal from "@/src/components/ExportModal.vue";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  sequenceId: { type: [String, Number], required: false },
});

const loading = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 15,
  total: 0,
});

const selection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const showExportModal = ref(false);

const handleSelectionUpdate = (newSelection) => {
  selection.value = newSelection;
};

const handleExportSuccess = () => {
  selection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
};

const linkedInLogFiltersOptions = ref(sequenceLinkedInLogFilters);
const linkedInLogFilterData = ref({});
const linkedInLogs = ref([]);

const fetchLinkedInAccounts = async () => {
  try {
    const response = await DashboardService.fetchLinkedInIntegrations();
    if (response.success && response.integrations) {
      const accounts = Object.entries(response.integrations).map(([id, name]) => ({
        id: parseInt(id),
        name: name,
      }));
      updateFilterOptions(
        linkedInLogFiltersOptions.value,
        "linkedin_integration_id",
        accounts
      );
    } else {
      console.error("Failed to fetch LinkedIn integrations:", response.errors);
      message.error("Failed to fetch LinkedIn integrations. Please try again.");
    }
  } catch (error) {
    console.error("Error occurred while fetching LinkedIn integrations: ", error);
    message.error("An error occurred while fetching LinkedIn integrations");
  }
};

const transformFiltersForBackend = (filters) => {
  const backendFilters = { ...filters };
  const messageEngagementFilters = [];

  if (filters.sequence_id) {
    backendFilters.sequence_ids = filters.sequence_id;
    delete backendFilters.sequence_id;
  }

  if (filters.linkedin_integration_id) {
    backendFilters.linkedin_integration_ids = filters.linkedin_integration_id;
    delete backendFilters.linkedin_integration_id;
  }

  if (filters.connection_request) {
    const requests = Array.isArray(filters.connection_request) 
      ? filters.connection_request 
      : [filters.connection_request];
    requests.forEach(status => {
      messageEngagementFilters.push({
        message_type: "INVITE",
        engagement_status: status,
      });
    });
    delete backendFilters.connection_request;
  }

  if (filters.message) {
    const messages = Array.isArray(filters.message) 
      ? filters.message 
      : [filters.message];
    messages.forEach(status => {
      messageEngagementFilters.push({
        message_type: "MESSAGE",
        engagement_status: status,
      });
    });
    delete backendFilters.message;
  }

  if (filters.inmail) {
    const inmails = Array.isArray(filters.inmail) 
      ? filters.inmail 
      : [filters.inmail];
    inmails.forEach(status => {
      messageEngagementFilters.push({
        message_type: "INMAIL",
        engagement_status: status,
      });
    });
    delete backendFilters.inmail;
  }

  if (messageEngagementFilters.length > 0) {
    backendFilters.message_engagement_filters = messageEngagementFilters;
  }

  if (filters.time_filter) {
    if (typeof filters.time_filter === 'object' && filters.time_filter.option === 'custom') {
      const { start_date, end_date } = filters.time_filter;
      if (start_date && end_date) {
        const formatDate = (dateStr) => {
          const [year, month, day] = dateStr.split('-');
          return `${day}_${month}_${year}`;
        };
        backendFilters.time_filter = `${formatDate(start_date)}-${formatDate(end_date)}`;
      }
    } else if (typeof filters.time_filter === 'object' && filters.time_filter.option) {
      backendFilters.time_filter = filters.time_filter.option;
    } else {
      backendFilters.time_filter = filters.time_filter;
    }
  }

  return backendFilters;
};

const applyFilters = async () => {
  loading.value = true;
  try {
    const backendFilters = transformFiltersForBackend({
      ...linkedInLogFilterData.value,
      sequence_id: props.sequenceId
        ? [props.sequenceId]
        : linkedInLogFilterData.value?.sequence_id || [],
    });

    const response = await DashboardService.fetchLinkedInLogs(
      pagination.value.current,
      pagination.value.pageSize,
      backendFilters
    );

    if (response?.success) {
      linkedInLogs.value = response.logs || [];
      pagination.value.total = response.total || linkedInLogs.value.length;
    } else {
      console.error("Failed to fetch LinkedIn logs:", response.errors);
      message.error("Failed to fetch LinkedIn logs. Please try again.");
    }
  } catch (error) {
    console.error("Error fetching LinkedIn logs:", error);
    message.error("An error occurred while fetching LinkedIn logs.");
  } finally {
    loading.value = false;
  }
};

const handleLinkedInLogFilterChange = async ({ filter, updated_values }) => {
  if (updated_values) {
    linkedInLogFilterData.value[filter] = updated_values;
  } else {
    delete linkedInLogFilterData.value[filter];
  }
  await applyFilters();
};

const handlePageChange = async (page) => {
  pagination.value = page;
  await applyFilters();
};

const clearAllFilters = () => {
  linkedInLogFilterData.value = {};
  applyFilters();
};

eventBus.on("sequence-linkedin-logs/export", () => {
  if (
    selection.value.selected_ids.length === 0 &&
    !selection.value.select_all
  ) {
    message.error("Please select LinkedIn logs to export");
    return;
  }

  showExportModal.value = true;
});

onMounted(() => {
  fetchLinkedInAccounts();
  applyFilters();
});

onBeforeUnmount(() => {
  eventBus.off("sequence-linkedin-logs/export");
});
</script>

